import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  obtenerTiposTablero,
  obtenerPublicaciones,
  Publicacion,
  TipoTablero,
} from "../../../../api/tableroInformativoService";
import { useAuth } from "../../../../context/AuthContext";
import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import {
  Loader2,
  Megaphone,
  Calendar,
  ChevronRight,
  Image as ImageIcon,
} from "lucide-react";
import Swal from "sweetalert2";

const ALLOWED_ROLES = ["AD", "AS"];

export default function TableroListPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const unidadCodigo = session?.unidad?.id || "";
  const rolCodigo = (session?.rol?.id || "").toUpperCase();
  const isAuthorized = ALLOWED_ROLES.includes(rolCodigo);

  const [tipos, setTipos] = useState<TipoTablero[]>([]);
  const [selectedTipo, setSelectedTipo] = useState<string>("");
  const [previousTipo, setPreviousTipo] = useState<string>("");
  const [publicaciones, setPublicaciones] = useState<Publicacion[]>([]);
  const [isLoadingTipos, setIsLoadingTipos] = useState(false);
  const [isLoadingPublicaciones, setIsLoadingPublicaciones] = useState(false);

  const hasTipos = useMemo(() => tipos.length > 0, [tipos]);

  useEffect(() => {
    if (!isAuthorized) return;
    loadTipos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized]);

  useEffect(() => {
    // Solo cargar si hay tipo seleccionado Y unidad codigo
    if (selectedTipo && unidadCodigo) {
      loadPublicaciones(selectedTipo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTipo, unidadCodigo]);

  const loadTipos = async () => {
    try {
      setIsLoadingTipos(true);
      const response = await obtenerTiposTablero();
      if (response.code === "1" && response.object_) {
        setTipos(response.object_);
        // Seleccionar "Venta" (VN) por defecto si existe
        const tipoVenta = response.object_.find(tipo => tipo.tti_codigo === "VN");
        if (tipoVenta && tipoVenta.tti_codigo) {
          setSelectedTipo(tipoVenta.tti_codigo);
        } else if (response.object_.length > 0) {
          // Si no hay "Venta", seleccionar el primero
          setSelectedTipo(response.object_[0].tti_codigo || "");
        }
      } else {
        throw new Error(response.message || "No se pudieron cargar los tipos");
      }
    } catch (error) {
      console.error("Error cargando tipos de tablero:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error instanceof Error ? error.message : "No se pudieron cargar los tipos de publicación.",
        confirmButtonColor: "#3d5a40",
      });
    } finally {
      setIsLoadingTipos(false);
    }
  };

  const loadPublicaciones = async (tipoCodigo: string) => {
    if (!tipoCodigo) {
      setPublicaciones([]);
      return;
    }
    
    try {
      setIsLoadingPublicaciones(true);
      const payload = {
        codigo: "",
        unidad_residencial_codigo: unidadCodigo,
        apartamento_codigo: "",
        tipo_publicacion_codigo: tipoCodigo, // Solo enviar si hay un tipo seleccionado
      };
      const response = await obtenerPublicaciones(payload);
      if (response.code === "1" && response.object_) {
        // Verificar si el array tiene elementos válidos (no solo objetos vacíos)
        const publicacionesValidas = response.object_.filter(
          (pub) => pub.tin_codigo && pub.tin_codigo !== 0 && pub.tin_titulo
        );
        if (publicacionesValidas.length > 0) {
          setPublicaciones(publicacionesValidas);
        } else {
          // Si viene vacío o solo objetos vacíos, volver a "Venta" (VN)
          setPublicaciones([]);
          const tipoVenta = tipos.find(t => t.tti_codigo === "VN");
          if (tipoVenta && tipoVenta.tti_codigo) {
            setSelectedTipo(tipoVenta.tti_codigo);
          }
          Swal.fire({
            icon: "info",
            title: "Sin publicaciones",
            text: response.message || "No se encontraron publicaciones para este tipo.",
            confirmButtonColor: "#3d5a40",
          });
        }
      } else if (response.code === "-1") {
        // Código -1: No se encontró información - volver a "Venta" (VN)
        setPublicaciones([]);
        const tipoVenta = tipos.find(t => t.tti_codigo === "VN");
        if (tipoVenta && tipoVenta.tti_codigo) {
          setSelectedTipo(tipoVenta.tti_codigo);
        }
        Swal.fire({
          icon: "info",
          title: "Sin información",
          text: response.message || "No se encontró información.",
          confirmButtonColor: "#3d5a40",
        });
      } else {
        throw new Error(response.message || "No se pudieron cargar las publicaciones");
      }
    } catch (error) {
      console.error("Error cargando publicaciones:", error);
      // En caso de error, volver a "Venta" (VN)
      setPublicaciones([]);
      const tipoVenta = tipos.find(t => t.tti_codigo === "VN");
      if (tipoVenta && tipoVenta.tti_codigo) {
        setSelectedTipo(tipoVenta.tti_codigo);
      }
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error instanceof Error ? error.message : "No se pudieron cargar las publicaciones.",
        confirmButtonColor: "#3d5a40",
      });
    } finally {
      setIsLoadingPublicaciones(false);
    }
  };

  const handleNavigate = (pub: Publicacion) => {
    const codigo = (pub.tin_codigo || pub.tab_codigo || pub.codigo || "").toString();
    const tipo = selectedTipo;
    if (!codigo) {
      Swal.fire({
        icon: "warning",
        title: "Publicación inválida",
        text: "No se encontró el código de la publicación.",
        confirmButtonColor: "#3d5a40",
      });
      return;
    }
    navigate(`/tablero/${tipo}/${codigo}`, { state: { publicacion: pub } });
  };

  if (!isAuthorized) {
    return (
      <div className="flex min-h-screen bg-gray-100 text-gray-900">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all">
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Acceso restringido</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">Esta sección está disponible solo para roles Administrador y Socio Administrador.</p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-100 text-gray-900">
      <Sidebar />

      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-military-600 flex items-center gap-2">
              <Megaphone className="h-6 w-6" />
              Tablero publicitario
            </h2>
            <p className="text-sm text-gray-600">Publicaciones de todos los apartamentos de la unidad.</p>
          </div>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Tipos de publicación</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingTipos && (
                <div className="flex items-center gap-2 text-military-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando tipos...
                </div>
              )}

              {!isLoadingTipos && !hasTipos && (
                <p className="text-sm text-gray-600">No hay tipos de publicaciones configurados.</p>
              )}

              <div className="flex flex-wrap gap-2 sm:gap-3">
                {tipos.map((tipo) => {
                  const isSelected = selectedTipo === (tipo.tti_codigo || "");
                  return (
                    <label
                      key={tipo.tti_codigo}
                      className={`flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected
                          ? "border-military-500 bg-military-50 text-military-700 shadow-sm"
                          : "border-gray-300 hover:border-military-400 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="tipoPublicacion"
                        className="text-military-600 focus:ring-2 focus:ring-military-500"
                        checked={isSelected}
                        onChange={() => {
                          if (selectedTipo) {
                            setPreviousTipo(selectedTipo);
                          }
                          setSelectedTipo(tipo.tti_codigo || "");
                        }}
                      />
                      <span className="text-sm sm:text-base font-medium text-gray-800 whitespace-nowrap">
                        {tipo.tti_nombre || "Sin nombre"}
                      </span>
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-military-500">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base sm:text-lg">Publicaciones</CardTitle>
                <p className="text-sm text-gray-600">
                  Selecciona una publicación para ver más detalles.
                </p>
              </div>
              {isLoadingPublicaciones && <Loader2 className="h-5 w-5 animate-spin text-military-600" />}
            </CardHeader>
            <CardContent>
              {isLoadingPublicaciones ? (
                <div className="flex items-center gap-2 text-military-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando publicaciones...
                </div>
              ) : publicaciones.length === 0 ? (
                <p className="text-sm text-gray-600">No hay publicaciones para este tipo.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                  {publicaciones.map((pub) => {
                    const codigo = pub.tin_codigo || pub.tab_codigo || pub.codigo;
                    const fecha = pub.tin_fecha || pub.fecha_publicacion || "";
                    const tipoNombre = pub.tin_tipo_nombre || pub.tipo_publicacion_nombre;
                    const imagen = pub.tin_ruta_foto || pub.foto_base64 || pub.ruta_foto || "";
                    const titulo = pub.tin_titulo || pub.titulo || "Sin título";
                    const descripcion = pub.tin_descripcion || pub.descripcion || "Sin descripción";
                    return (
                      <div
                        key={`${codigo}`}
                        className="border-2 border-gray-200 rounded-xl shadow-sm bg-white hover:shadow-lg hover:border-military-300 transition-all cursor-pointer flex flex-col overflow-hidden"
                        onClick={() => handleNavigate(pub)}
                      >
                        <div className="relative h-48 sm:h-52 bg-gray-100 flex items-center justify-center overflow-hidden">
                          {imagen ? (
                            <img 
                              src={imagen} 
                              alt={titulo} 
                              className="w-full h-full object-cover" 
                            />
                          ) : (
                            <div className="flex flex-col items-center justify-center w-full h-full text-gray-400 p-4">
                              <ImageIcon className="h-12 w-12 sm:h-16 sm:w-16 mb-2" />
                              <span className="text-xs sm:text-sm font-medium">NO HAY IMAGEN</span>
                            </div>
                          )}
                          {tipoNombre && (
                            <span className="absolute top-2 right-2 px-2 py-1 text-xs font-semibold rounded-full bg-green-500 text-white shadow-md">
                              {tipoNombre}
                            </span>
                          )}
                        </div>
                        <div className="p-4 sm:p-5 flex-1 flex flex-col gap-2 sm:gap-3">
                          <h3 className="text-base sm:text-lg font-bold text-gray-900 line-clamp-2 min-h-[3rem]">
                            {titulo}
                          </h3>
                          {fecha && (
                            <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-500">
                              <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                              <span className="truncate">{fecha}</span>
                            </div>
                          )}
                          <p className="text-sm sm:text-base text-gray-600 line-clamp-2 flex-1">
                            {descripcion}
                          </p>
                          <div className="flex items-center text-military-600 font-semibold text-sm sm:text-base gap-1 mt-2 pt-2 border-t border-gray-100">
                            Ver detalle
                            <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

